import {
  health, deviceStatus, serverStatus, worstStatus, backupSummary, timeAgoEpoch,
  storageColor, diskInfo,
} from "./dashboardHelpers.js";
import { StatusBadge, StatusDot, Meter } from "./DashboardParts.jsx";

// ══════════════════════════════════════════════════════
//  Carte "client" — tout ce qui est supervisé chez un même client
//  (serveurs, NAS, UDM) en un seul bloc. Utilisée par Tour de contrôle
//  et Vue d'ensemble.
// ══════════════════════════════════════════════════════

function Row({ icon, title, status, children }) {
  return (
    <div style={{ background: "var(--navy-mid)", border: "1px solid var(--border)", borderRadius: 10, padding: "9px 11px", display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, fontWeight: 700, color: "var(--text-1)", minWidth: 0 }}>
        <i className={`bi ${icon}`} style={{ color: "var(--text-3)" }} />
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>{title}</span>
        <StatusDot status={status} size={8} />
      </div>
      {children}
    </div>
  );
}

const line = { fontSize: 11.5, color: "var(--text-2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

export default function ClientCard({ group }) {
  const { company, status, servers, nas, gateways } = group;
  const online = servers.filter(s => s.status === "ONLINE").length;
  const serversStatus = worstStatus(servers.map(serverStatus));
  const problemServers = servers.filter(s => serverStatus(s) !== "OK").slice(0, 3);

  return (
    <div style={{
      background: "var(--card-bg)", borderRadius: 14, padding: "14px 16px", minWidth: 0,
      border: `1px solid ${status === "OK" ? "var(--border)" : health(status).color}`,
      display: "flex", flexDirection: "column", gap: 10,
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <div style={{ fontWeight: 700, fontSize: 14.5, color: "var(--text-1)" }}>{company}</div>
        <StatusBadge status={status} label={health(status).long} />
      </div>

      {servers.length > 0 && (
        <Row icon="bi-pc-display-horizontal" title="Serveurs" status={serversStatus}>
          <div style={line}><strong style={{ color: "var(--text-1)" }}>{online}/{servers.length}</strong> en ligne</div>
          {problemServers.map(s => (
            <div key={s.id} style={{ ...line, color: health(serverStatus(s)).color, display: "flex", gap: 6, alignItems: "center" }}>
              <StatusDot status={serverStatus(s)} size={6} />{s.hostname}{s.status !== "ONLINE" ? " — hors ligne" : (() => { const d = diskInfo(s); return d ? ` — disque ${d.pct} %` : ""; })()}
            </div>
          ))}
        </Row>
      )}

      {nas.map(n => {
        const st = deviceStatus(n);
        const { latest } = backupSummary(n.backupJobs);
        return (
          <Row key={n.id} icon="bi-hdd-network" title={n.name} status={st}>
            <Meter label="Stockage" value={n.storagePercent} color={storageColor(n.storagePercent)} height={5} />
            <div style={{ ...line, color: n.raidStatus && n.raidStatus.toLowerCase() !== "normal" ? "var(--danger)" : undefined }}>
              RAID {n.raidStatus || "—"}{n.temperatureC != null ? ` · ${n.temperatureC} °C` : ""}
            </div>
            <div style={{ ...line, color: latest && latest.status !== "OK" ? health(latest.status).color : undefined }}>
              {latest
                ? `Sauvegarde ${latest.status === "OK" ? "réussie" : latest.status === "WARNING" ? "partielle" : "en échec"} · ${timeAgoEpoch(latest.lastRunEpoch)}`
                : (n.backupJobs || []).length ? "Sauvegardes : horodatage indisponible" : "Aucune sauvegarde détectée"}
            </div>
            {n.lastError && <div style={{ ...line, color: "var(--danger)" }}>Synchro en échec</div>}
          </Row>
        );
      })}

      {gateways.map(g => {
        const st = deviceStatus(g);
        return (
          <Row key={g.id} icon="bi-router" title={g.name} status={st}>
            <div style={{ ...line, color: st === "CRITICAL" ? "var(--danger)" : undefined }}>
              WAN {g.wanStatus || "—"}{g.latencyMs != null ? ` · ${g.latencyMs} ms` : ""}
            </div>
            <div style={line}>
              {g.connectedClients != null ? `${g.connectedClients} clients` : "—"}
              {g.uptimePercent != null ? ` · disponibilité ${g.uptimePercent} %` : ""}
            </div>
            {g.lastError && <div style={{ ...line, color: "var(--danger)" }}>Synchro en échec</div>}
          </Row>
        );
      })}
    </div>
  );
}